/**
 * Tarayıcı hata takibi (S62; 01 §9.3 O10, YH-S09, SEC-23): panelin (yönetim, çalışan PWA'sı), QR Ekranı/Kiosk girişinin ve backoffice'in
 * yakalanmayan hataları Sentry protokolüyle kendi GlitchTip sunucumuza gider. Değerler çalışma zamanındadır (`/app-config.json`: nginx giriş
 * betiği `ERROR_TRACKING_DSN`, `ERROR_TRACKING_ENVIRONMENT` ve imajın commit SHA'sını yazar); derlemede sır yoktur.
 *
 * Bu giriş her sayfanın paketindedir ve küçüktür: açılışta hata dinleyicilerini kurar, yapılandırmayı okur. DSN yoksa dinleyiciler kalkar, SDK
 * hiç inmez ve dışarı istek gitmez. DSN varsa SDK tembel paketle iner (`client.ts`); açılış sırasında yakalanan hatalar (en çok 20) ona verilir.
 * Kişisel veri ve jeton temizliği `scrub.ts`'tedir.
 */

import type { ErrorTrackingApp, ErrorTrackingConfig, ErrorTrackingRuntimeConfig } from './types.ts';

export type { ErrorTrackingApp, ErrorTrackingConfig, ErrorTrackingRuntimeConfig } from './types.ts';

/** Sentry DSN'i: `https://<anahtar>@<sunucu>[:port][/yol]/<proje>`; nginx giriş betiği aynı biçimi doğrular. */
const DSN = /^https?:\/\/[A-Za-z0-9]+@[A-Za-z0-9]([A-Za-z0-9.-]*[A-Za-z0-9])?(?::\d{1,5})?(?:\/[A-Za-z0-9._-]+)*\/\d+$/;
const ENVIRONMENT = /^[a-z][a-z0-9_-]{0,31}$/;
const RELEASE = /^[A-Za-z0-9._-]{1,64}$/;
const MAX_PENDING = 20;

let pending: unknown[] | null = null;
let capture: ((error: unknown) => void) | null = null;

/** Yapılandırmanın hata takibi alanları; DSN yoksa ya da geçersizse `null` (hata takibi kapalı). */
export function errorTrackingConfigOf(body: unknown): ErrorTrackingConfig | null {
  const value = (body ?? {}) as Partial<Record<keyof ErrorTrackingRuntimeConfig, unknown>>;
  const dsn = value.errorTrackingDsn;
  if (typeof dsn !== 'string' || !DSN.test(dsn)) {
    return null;
  }

  const environment = value.errorTrackingEnvironment;
  const release = value.release;
  return {
    dsn,
    environment: typeof environment === 'string' && ENVIRONMENT.test(environment) ? environment : 'production',
    release: typeof release === 'string' && RELEASE.test(release) && release !== 'local' ? release : undefined,
  };
}

/**
 * Hata sınırının (rota `errorElement`) yakaladığı hatayı bildirir; hata takibi kapalıysa hiçbir şey yapmaz. Açılış sürerken gelen hata
 * bekletilir.
 */
export function reportError(error: unknown): void {
  if (capture) {
    capture(error);
  } else if (pending && pending.length < MAX_PENDING) {
    pending.push(error);
  }
}

/** Hata takibini başlatır (girişte bir kez). */
export function startErrorTracking(app: ErrorTrackingApp): void {
  if (pending || capture || typeof window === 'undefined') {
    return;
  }

  pending = [];
  const onError = (event: ErrorEvent) => reportError(event.error ?? event.message);
  const onRejection = (event: PromiseRejectionEvent) => reportError(event.reason);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  void fetch('/app-config.json', { headers: { Accept: 'application/json' } })
    .then((response) => (response.ok ? response.json() : null))
    .then(async (body: unknown) => {
      const config = errorTrackingConfigOf(body);
      return config ? (await import('./client.ts')).startClient(config, app) : null;
    })
    .catch(() => null)
    .then((client) => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      const queued = pending ?? [];
      pending = null;
      capture = client;
      if (client) {
        queued.forEach(client);
      }
    });
}
